import { Body } from "@sprout/typography";
import useTranslation from "@hooks/use_translation.ts";
import type { ProfileData } from "@/queries/use_profile.ts";
import type { Education } from "@components/quick_apply/education_section";

type Props = { data: ProfileData };

const byMostRecent = (a: Education, b: Education) =>
  (b.endYear ?? Infinity) - (a.endYear ?? Infinity) || (b.endMonth ?? 0) - (a.endMonth ?? 0);

const hasDisplayableFields = (education: Education) =>
  Boolean(
    education.school?.label ||
      education.degree?.label ||
      education.discipline?.label ||
      education.startYear,
  );

export const isEducationEmpty = (data: ProfileData): boolean =>
  !(data.educations ?? []).some(hasDisplayableFields);

const EducationBody = ({ data }: Props) => {
  const { t } = useTranslation("user", { keyPrefix: "profile_sections.education" });
  const educations = (data.educations ?? []).filter(hasDisplayableFields).sort(byMostRecent);

  if (educations.length === 0) return null;

  return (
    <ol className="m-0 flex list-none flex-col p-0">
      {educations.map((education, index) => {
        const heading = [education.degree?.label, education.discipline?.label]
          .filter(Boolean)
          .join(", ");
        const range = education.startYear
          ? `${education.startYear} - ${education.endYear ?? t("current")}`
          : "";
        const subtitle = [education.school?.label, range].filter(Boolean).join(" • ");

        return (
          <li
            key={education.key ?? index}
            className={index > 0 ? "border-border mt-3 border-t pt-3" : undefined}
          >
            {heading && <Body medium>{heading}</Body>}
            {subtitle && <Body metadata>{subtitle}</Body>}
          </li>
        );
      })}
    </ol>
  );
};

export default EducationBody;
